// iter196 — Staff Account Statement drawer.
//
// Full-height right-side drawer that reads /team/crew/ledger/{id}/statement
// and renders a bank-style account: header (staff + big current balance),
// summary tiles, chronological statement rows with running balance,
// [+ Add Payment] and [± Adjustment] actions. All writes go through the
// existing quick-pay + new adjustment endpoints — accounting is unchanged.
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { inr } from "@/lib/utils";
import { toast } from "sonner";
import { X, Plus, Minus, Loader2, Search, ArrowUpRight, ArrowDownRight, Receipt } from "lucide-react";

const MODE_OPTS = [
  { v: "cash", l: "Cash" }, { v: "upi", l: "UPI" },
  { v: "bank", l: "Bank Transfer" }, { v: "cheque", l: "Cheque" },
  { v: "other", l: "Other" },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "earning", label: "Earnings" },
  { key: "payment", label: "Payments" },
  { key: "adjustment", label: "Adjustments" },
];

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

export default function StaffAccountDrawer({ open, staffId, onClose, onLedgerRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [action, setAction] = useState(null); // "pay" | "adj+" | "adj-"

  const load = async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const r = await api.get(`/team/crew/ledger/${staffId}/statement`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load account");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && staffId) load(); /* eslint-disable-line */ }, [open, staffId]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { if (action) setAction(null); else onClose?.(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, action, onClose]);

  const filtered = useMemo(() => {
    if (!data?.entries) return [];
    let rows = data.entries;
    if (filter !== "all") rows = rows.filter((e) => e.type === filter);
    const q = query.trim().toLowerCase();
    if (q) rows = rows.filter((e) => {
      const bits = [e.description, e.meta?.reference, e.meta?.mode, e.meta?.notes, e.meta?.event]
        .filter(Boolean).join(" ").toLowerCase();
      return bits.includes(q);
    });
    return rows;
  }, [data, filter, query]);

  if (!open) return null;
  const s = data?.staff;
  const sm = data?.summary || {};
  const isDue = (sm.current_balance || 0) > 0;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/50 backdrop-blur-sm"
         onClick={onClose} role="dialog" aria-modal="true">
      <div className="bg-stone-50 w-full sm:max-w-lg h-full shadow-2xl overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()} data-testid="staff-account-drawer">

        {/* Header */}
        <div className="bg-white px-5 py-4 border-b border-stone-200">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {s?.photo_url ? (
                <img src={s.photo_url} alt="" className="w-11 h-11 rounded-full object-cover ring-1 ring-stone-200" />
              ) : (
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-stone-200 to-stone-300 flex items-center justify-center text-stone-600 font-semibold">
                  {(s?.name || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="font-heading font-semibold text-lg truncate">{s?.name || "Staff"}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-stone-500 mt-0.5">
                  {[s?.primary_skill, s?.employment_type?.replace("_", " ")].filter(Boolean).join(" · ")}
                </div>
                {s?.whatsapp && (
                  <div className="text-[11px] text-stone-500 mt-0.5">{s.whatsapp}</div>
                )}
              </div>
            </div>
            <button className="p-1 rounded hover:bg-stone-100" onClick={onClose} aria-label="Close"
                    data-testid="staff-account-close">
              <X className="w-4 h-4" strokeWidth={1.6} />
            </button>
          </div>

          {/* Rates */}
          <div className="mt-3 flex items-center gap-4 text-[10px] uppercase tracking-[0.12em] text-stone-500">
            <span>Shoot ₹{s?.base_shoot_rate ?? 3000}</span>
            <span>·</span>
            <span>Assist ₹{s?.assist_rate ?? 1200}</span>
          </div>

          {/* Balance */}
          <div className="mt-4 rounded-lg p-4 bg-gradient-to-br from-stone-50 to-stone-100/60 border border-stone-200">
            <div className="text-[10px] uppercase tracking-[0.16em] text-stone-500 font-semibold">
              {isDue ? "Amount Due" : "Current Balance"}
            </div>
            <div className={`font-heading text-3xl font-semibold tabular-nums mt-1 ${
              isDue ? "text-rose-800" : "text-emerald-800"
            }`} data-testid="staff-account-balance">
              {inr(Math.abs(sm.current_balance || 0))}
              {(sm.current_balance || 0) < 0 && <span className="text-sm text-stone-500 font-normal ml-2">(overpaid)</span>}
            </div>
            <div className="grid grid-cols-4 gap-3 mt-4 pt-3 border-t border-stone-200/60">
              <SumTile label="Earned" value={sm.total_earnings} accent="emerald" />
              <SumTile label="Paid" value={sm.total_paid} accent="stone" />
              <SumTile label="Adjust" value={sm.total_adjustments} accent="amber" signed />
              <SumTile label="Shoots" value={sm.shoot_count} raw />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-3 flex gap-2">
            <button className="btn btn-primary btn-xs flex-1" onClick={() => setAction("pay")}
                    data-testid="staff-account-pay-btn">
              <Plus className="w-3 h-3" strokeWidth={2.4} /> Add Payment
            </button>
            <button className="btn btn-outline btn-xs" onClick={() => setAction("adj+")}
                    data-testid="staff-account-adj-plus">
              <Plus className="w-3 h-3" strokeWidth={2.4} /> Adjustment
            </button>
            <button className="btn btn-outline btn-xs" onClick={() => setAction("adj-")}
                    data-testid="staff-account-adj-minus">
              <Minus className="w-3 h-3" strokeWidth={2.4} /> Adjustment
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white px-5 py-2 border-b border-stone-200 flex flex-wrap items-center gap-1">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
                    className={`px-2.5 py-1 rounded-full text-[10px] tracking-wide border transition ${
                      filter === f.key
                        ? "bg-stone-900 text-stone-50 border-stone-900"
                        : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                    }`}
                    data-testid={`staff-account-filter-${f.key}`}>
              {f.label}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="w-3 h-3 text-stone-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input type="text" className="input py-1 pl-7 text-xs w-32 sm:w-40" placeholder="Search…"
                   value={query} onChange={(e) => setQuery(e.target.value)}
                   data-testid="staff-account-search" />
          </div>
        </div>

        {/* Statement */}
        <div className="flex-1 overflow-y-auto px-5 py-3" data-testid="staff-account-statement">
          {loading ? (
            <div className="text-center py-10 text-xs text-stone-400"><Loader2 className="inline w-4 h-4 animate-spin mr-2" />Loading account…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-xs text-stone-400 italic">No entries match.</div>
          ) : (
            filtered.map((e) => <StatementRow key={e.id} entry={e} />)
          )}
        </div>
      </div>

      {/* Action modal */}
      {action && (
        <ActionModal
          staffId={staffId}
          staff={s}
          summary={sm}
          action={action}
          onClose={() => setAction(null)}
          onSaved={async () => {
            setAction(null);
            await load();
            onLedgerRefresh?.();
          }}
        />
      )}
    </div>
  );
}

function SumTile({ label, value, accent = "stone", signed = false, raw = false }) {
  const colorClass = accent === "emerald" ? "text-emerald-700"
    : accent === "amber" ? "text-amber-700"
    : "text-stone-700";
  const display = raw ? (value ?? 0) : (signed
    ? `${(value || 0) >= 0 ? "+" : ""}${inr(value || 0)}`
    : inr(value || 0));
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.12em] text-stone-500">{label}</div>
      <div className={`font-mono text-xs font-semibold tabular-nums mt-0.5 ${colorClass}`}>{display}</div>
    </div>
  );
}

function StatementRow({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const isEarn = entry.type === "earning";
  const isPay = entry.type === "payment";
  const isAdj = entry.type === "adjustment";
  const Arrow = entry.amount >= 0 ? ArrowUpRight : ArrowDownRight;
  const amtColor = entry.amount >= 0 ? "text-emerald-700" : "text-rose-700";
  return (
    <div className="border-b border-stone-100 last:border-0"
         data-testid={`statement-row-${entry.id}`}>
      <button type="button" onClick={() => setExpanded((v) => !v)}
              className="w-full flex items-center justify-between gap-3 py-3 text-left hover:bg-stone-50/60 rounded px-2 -mx-2 transition">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-1 h-8 rounded-full ${isEarn ? "bg-emerald-500" : isPay ? "bg-stone-400" : "bg-amber-500"}`} />
          <div className="min-w-0">
            <div className="text-sm font-medium text-stone-800 truncate">{entry.description}</div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-stone-500 mt-0.5 flex gap-2 items-center">
              <span>{fmtDate(entry.ts)}</span>
              <span className={`px-1.5 py-0.5 rounded ${
                isEarn ? "bg-emerald-50 text-emerald-700" :
                isPay ? "bg-stone-100 text-stone-700" :
                "bg-amber-50 text-amber-700"
              }`}>{entry.type}</span>
              {entry.meta?.mode && <span className="text-stone-400 uppercase">· {entry.meta.mode}</span>}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className={`inline-flex items-center gap-1 font-mono text-sm font-semibold tabular-nums ${amtColor}`}>
            <Arrow className="w-3 h-3" strokeWidth={2} />
            {entry.amount >= 0 ? "+" : "−"}{inr(Math.abs(entry.amount))}
          </div>
          <div className="text-[10px] text-stone-500 tabular-nums mt-0.5" data-testid={`statement-balance-${entry.id}`}>
            Bal · {inr(entry.balance_after || 0)}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="text-[11px] text-stone-600 bg-stone-50 rounded p-3 mb-2 grid grid-cols-2 gap-y-1 gap-x-3">
          {entry.meta?.reference && <><span className="text-stone-400">Reference</span><span>{entry.meta.reference}</span></>}
          {entry.meta?.notes && <><span className="text-stone-400">Notes</span><span className="italic">{entry.meta.notes}</span></>}
          {entry.meta?.event && <><span className="text-stone-400">Event</span><span>{entry.meta.event}</span></>}
          {entry.meta?.job_role && <><span className="text-stone-400">Role</span><span>{entry.meta.job_role}</span></>}
          {entry.meta?.created_by && <><span className="text-stone-400">Recorded by</span><span>{entry.meta.created_by}</span></>}
          {entry.meta?.created_at && <><span className="text-stone-400">Recorded at</span><span>{fmtDate(entry.meta.created_at)}</span></>}
          <span className="text-stone-400">Previous balance</span><span className="tabular-nums">{inr((entry.balance_after || 0) - entry.amount)}</span>
          <span className="text-stone-400">Balance after</span><span className="tabular-nums font-semibold">{inr(entry.balance_after || 0)}</span>
        </div>
      )}
    </div>
  );
}

function ActionModal({ staffId, staff, summary, action, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isPay = action === "pay";
  const isPlus = action === "adj+";
  const title = isPay ? "Add Payment" : isPlus ? "Add Bonus / Extra" : "Recover / Deduct";
  const currentBal = summary.current_balance || 0;
  const amt = Number(amount) || 0;
  const signedAmt = isPay ? -Math.abs(amt) : isPlus ? Math.abs(amt) : -Math.abs(amt);
  const newBal = Math.round((currentBal + signedAmt) * 100) / 100;
  const canSave = amt > 0 && !busy && (isPay || reason.trim().length > 0);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (isPay) {
        await api.post(`/team/crew/ledger/${staffId}/quick-pay`, {
          amount: amt, mode, reference: reference.trim(),
          notes: reason.trim() || undefined,
        });
        toast.success(`Payment of ${inr(amt)} recorded`);
      } else {
        await api.post(`/team/crew/ledger/${staffId}/adjustment`, {
          amount: signedAmt, reason: reason.trim(),
        });
        toast.success(`Adjustment ${isPlus ? "+" : "−"}${inr(amt)} saved`);
      }
      onSaved?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onClose}
         role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl overflow-hidden"
           onClick={(e) => e.stopPropagation()} data-testid="staff-account-action-modal">
        <div className="px-5 py-4 border-b border-stone-200 flex items-center gap-2">
          <Receipt className={`w-4 h-4 ${isPay ? "text-emerald-700" : isPlus ? "text-amber-700" : "text-rose-700"}`} strokeWidth={2} />
          <div>
            <div className="font-heading font-semibold">{title}</div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500">{staff?.name}</div>
          </div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <label className="editorial-eyebrow block mb-1">Amount <span className="text-rose-700">*</span></label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">₹</span>
              <input type="number" min={0} className="input pl-6 text-right font-mono"
                     value={amount} onChange={(e) => setAmount(e.target.value)}
                     data-testid="action-amount" />
            </div>
          </div>
          {isPay && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="editorial-eyebrow block mb-1">Mode</label>
                <select className="input" value={mode} onChange={(e) => setMode(e.target.value)}
                        data-testid="action-mode">
                  {MODE_OPTS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
              </div>
              <div>
                <label className="editorial-eyebrow block mb-1">Reference</label>
                <input type="text" className="input" value={reference}
                       onChange={(e) => setReference(e.target.value)} maxLength={120}
                       data-testid="action-reference" />
              </div>
            </div>
          )}
          <div>
            <label className="editorial-eyebrow block mb-1">
              {isPay ? "Notes" : "Reason"} {isPay ? "" : <span className="text-rose-700">*</span>}
            </label>
            <input type="text" className="input" value={reason}
                   onChange={(e) => setReason(e.target.value)} maxLength={300}
                   placeholder={isPay ? "e.g. Week 1 salary" : "e.g. Extra travel / Advance recovery"}
                   data-testid="action-reason" />
          </div>
          <div className="rounded-lg p-3 bg-stone-50 border border-stone-200 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-stone-500">Current Balance</span><span className="font-mono tabular-nums">{inr(currentBal)}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">{isPay ? "Payment" : "Adjustment"}</span>
              <span className={`font-mono tabular-nums ${signedAmt >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {signedAmt >= 0 ? "+" : "−"}{inr(Math.abs(signedAmt))}
              </span>
            </div>
            <div className="flex justify-between font-semibold pt-1 border-t border-stone-200">
              <span>New Balance</span>
              <span className={`font-mono tabular-nums ${newBal >= 0 ? "text-rose-800" : "text-emerald-800"}`}
                    data-testid="action-preview-balance">
                {inr(newBal)}
              </span>
            </div>
          </div>
          {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-stone-200 bg-stone-50/60 flex items-center justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave} onClick={save}
                  data-testid="action-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Confirm {isPay ? "Payment" : "Adjustment"}
          </button>
        </div>
      </div>
    </div>
  );
}
